import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import SharedFilter from '../components/SharedFilter';
import bookingService, { Booking } from '../services/bookingService';
import { incomeService, AvailableIncome } from '../services/incomeService';
import { projectService, Project } from '../services/projectService';
import { unitService } from '../services/unitService';
import { usePageTitle } from '../hooks/usePageTitle';
import { useHiddenMode } from '../contexts/HiddenModeContext';
import { formatDate, formatDateForInput, getMaxTransactionDate } from '../utils/formatters';
import { formatMainDBAmount, formatSystemsDBAmount, formatTotalAmount } from '../utils/formatAmount';
import { filterUnitsBySearch, formatUnitDisplay } from '../utils/unitSearch';
import '../css/pages/AddIncome.css';

const AddIncome: React.FC = () => {
  usePageTitle();
  const { isHiddenModeActive } = useHiddenMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const isEditMode = !!id; // Edit mode when id query parameter is present
  const isViewMode = window.location.pathname.includes('/view/');
  const mode = isEditMode ? 'edit' : isViewMode ? 'view' : 'add';
  
  // Legacy search states (replaced by SharedFilter)
  const [searchType, setSearchType] = useState<'customer' | 'unit'>('customer');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [wingOptions, setWingOptions] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<Booking[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Rejection info shown when editing a rejected income
  const [rejectedInfo, setRejectedInfo] = useState<{ reason: string } | null>(null);

  // New filter states (mirror booking screen)
  const [showFilters, setShowFilters] = useState(true);
  const [projectFilter, setProjectFilter] = useState('');
  const [wingFilter, setWingFilter] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [mobileFilter, setMobileFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    project: '',
    wing: '',
    unit: '',
    customer: '',
    mobile: '',
  });
  const [hasAppliedOnce, setHasAppliedOnce] = useState(false);

  // Dev-only: log computed styles to verify alignment and font sizes in UI
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // small timeout to ensure DOM painted
      const t = setTimeout(() => {
        const cn = document.querySelector('.add-income-page .customer-name') as HTMLElement | null;
        if (cn) {
          const cs = window.getComputedStyle(cn);
          console.log('[AddIncome] customer-name computed', { textAlign: cs.textAlign, fontSize: cs.fontSize, fontWeight: cs.fontWeight });
          const td = cn.closest('td');
          if (td) {
            const csTd = window.getComputedStyle(td);
            console.log('[AddIncome] customer TD computed', { textAlign: csTd.textAlign });
          }
        } else {
          console.log('[AddIncome] customer-name not found');
        }
        const un = document.querySelector('.add-income-page .unit-number') as HTMLElement | null;
        if (un) {
          const csu = window.getComputedStyle(un);
          console.log('[AddIncome] unit-number computed', { fontSize: csu.fontSize, fontWeight: csu.fontWeight });
        }
      }, 0);
      return () => clearTimeout(t);
    }
  }, [searchResults.length]);
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentDate: formatDateForInput(new Date()),
    paymentMethod: 'cheque' as 'cheque' | 'neft' | 'imps' | 'rtgs' | 'upi' | 'mb' | 'transfer' | 'other',
    referenceNumber: '',
    bankName: '',
    description: '',
    receiptName: '',
    receiptNo: '',
    chequeReceivedDate: formatDateForInput(new Date()),
    instrumentDate: '', // Blank initially, user must enter manually
    chequeDepositDate: '',
    remarks: '',
    paymentTerms: ''
  });
  
  // State for custom payment method when "Other" is selected
  const [customPaymentMethod, setCustomPaymentMethod] = useState('');

  // Return allocation state
  const [availableIncomes, setAvailableIncomes] = useState<AvailableIncome[]>([]);
  const [allocations, setAllocations] = useState<Record<number, number>>({});
  const [showAllocationUI, setShowAllocationUI] = useState(false);
  const [allocationErrors, setAllocationErrors] = useState<string[]>([]);

  // Load available incomes when amount becomes negative
  useEffect(() => {
    const amt = parseFloat(paymentForm.amount);
    if (amt < 0 && selectedBooking) {
      setShowAllocationUI(true);
      setAllocationErrors([]);
      incomeService.getAvailableIncomesForReturn(selectedBooking.id)
        .then(data => {
          setAvailableIncomes(data);
          if (Object.keys(allocations).length === 0) {
            const initial: Record<number, number> = {};
            data.forEach(inc => { initial[inc.id] = 0; });
            setAllocations(initial);
          }
        })
        .catch(err => console.error('Failed to load available incomes:', err));
    } else {
      setShowAllocationUI(false);
      setAllocations({});
      setAvailableIncomes([]);
    }
  }, [paymentForm.amount, selectedBooking]);

  const totalAllocated = Object.values(allocations).reduce((sum, v) => sum + v, 0);
  const absAmount = Math.abs(parseFloat(paymentForm.amount) || 0);
  const isAllocationValid = totalAllocated > 0 && Math.abs(totalAllocated - absAmount) <= 0.01;

  const handleMark = (incomeId: number, checked: boolean, availableBalance: number) => {
    setAllocationErrors([]);
    if (!checked) {
      setAllocations(prev => ({ ...prev, [incomeId]: 0 }));
      return;
    }
    const currentTotal = Object.values(allocations).reduce((s, v) => s + v, 0) - (allocations[incomeId] || 0);
    const remaining = absAmount - currentTotal;
    if (remaining <= 0) return;
    const toAllocate = Math.min(availableBalance, remaining);
    setAllocations(prev => ({ ...prev, [incomeId]: toAllocate }));
  };

  useEffect(() => {
    loadProjects();
    
    // Check if there's a booking ID in URL params (coming from booking details page)
    const bookingId = searchParams.get('bookingId');
    if (bookingId) {
      loadBookingById(parseInt(bookingId));
    }
    
    // Load existing income data for edit/view mode
    if (mode !== 'add' && id) {
      loadIncome(parseInt(id));
    }

    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        setShowProjectDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchParams, mode, id]);

  const loadProjects = async () => {
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
      setError(errorMessage);
      console.error('Projects loading error:', err);
    }
  };

  // Load wing options whenever selected project changes
  useEffect(() => {
    const fetchWings = async () => {
      try {
        if (!projectFilter) {
          setWingOptions([]);
          return;
        }
        const { units } = await unitService.getUnits(parseInt(projectFilter), { page: 1, limit: 500 });
        const wings = Array.from(new Set((units || []).map(u => (u.wing || '').trim()).filter(Boolean)));
        setWingOptions(wings);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[AddIncome] Failed to load wings', e);
        }
        setWingOptions([]);
      }
    };
    fetchWings();
  }, [projectFilter]);

  const loadBookingById = async (bookingId: number) => {
    try {
      setLoading(true);
      setError('');
      const booking = await bookingService.getBookingById(bookingId);
      setSelectedBooking(booking);
      setShowPaymentForm(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load booking details';
      setError(errorMessage);
      console.error('Booking loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadIncome = async (incomeId: number) => {
    try {
      setLoading(true);
      setError('');
      const income = await incomeService.getIncomeById(incomeId);
      
      // Surface rejection reason to the user when re-editing a rejected entry
      if (income.status === 'rejected') {
        setRejectedInfo({ reason: income.rejectionReason || 'No reason provided' });
      } else {
        setRejectedInfo(null);
      }
      
      // Check if payment method is a standard one or custom
      const standardMethods = ['cheque', 'neft', 'imps', 'rtgs', 'upi', 'mb', 'transfer'];
      const isCustomMethod = !standardMethods.includes(income.paymentMethod.toLowerCase());
      
      // Set payment form data with proper validation
      setPaymentForm({
        amount: income.amount.toString(),
        paymentDate: formatDateForInput(income.paymentDate),
        paymentMethod: isCustomMethod ? 'other' : income.paymentMethod as any,
        referenceNumber: income.referenceNumber || '',
        bankName: income.bankName || '',
        description: income.description || '',
        receiptName: income.receiptName || '',
        receiptNo: income.receiptNo || '',
        chequeReceivedDate: income.chequeReceivedDate ? formatDateForInput(income.chequeReceivedDate) : '',
        instrumentDate: income.instrumentDate ? formatDateForInput(income.instrumentDate) : '',
        chequeDepositDate: income.chequeDepositDate ? formatDateForInput(income.chequeDepositDate) : '',
        remarks: income.remarks || '',
        paymentTerms: (income as any).paymentTerms || ''
      });
      
      // If it's a custom payment method, set it in the custom field
      if (isCustomMethod) {
        setCustomPaymentMethod(income.paymentMethod);
      }
      
      // Load the associated booking
      if (income.bookingId) {
        await loadBookingById(income.bookingId);
      }
      
      setShowPaymentForm(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load income details';
      setError(errorMessage);
      console.error('Income loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    // Deprecated direct search; use applied filters instead
    return applyFilters();
  };

  type Applied = { project: string; wing: string; unit: string; customer: string; mobile: string };

  const applyFilters = async (override?: Partial<Applied>) => {
    setSearchLoading(true);
    setError('');
    try {
      // Resolve filters from current inputs unless an override is provided (no duplicate keys to satisfy linter)
      const filters: Applied = {
        project: projectFilter,
        wing: wingFilter,
        unit: unitFilter,
        customer: customerFilter,
        mobile: mobileFilter,
      };
      if (override) {
        if (override.project !== undefined) filters.project = override.project;
        if (override.wing !== undefined) filters.wing = override.wing;
        if (override.unit !== undefined) filters.unit = override.unit;
        if (override.customer !== undefined) filters.customer = override.customer;
        if (override.mobile !== undefined) filters.mobile = override.mobile;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log('[AddIncome] applying filters', filters);
      }

      const result = await bookingService.getBookings({
        status: 'active',
        page: 1,
        limit: 50,
        projectId: filters.project ? parseInt(filters.project) : undefined,
        wing: filters.wing || undefined,
        unitNo: filters.unit || undefined,
        memberName: filters.customer || undefined,
        mobile: filters.mobile || undefined,
      });

      setSearchResults(result.bookings || []);
      setSelectedBooking(null);
      setShowPaymentForm(false);

      if (!result.bookings || result.bookings.length === 0) {
        setError('No active bookings found for the given filters');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load bookings';
      setError(errorMessage);
      console.error('Filter apply error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectBooking = async (booking: Booking) => {
    setSelectedBooking(booking);
    setShowPaymentForm(true);
    setSearchResults([]);
    setError('');

    // Default receipt name to a booking customer (primary preferred)
    const primary = booking.applicants.find(a => a.isPrimary) || booking.applicants[0];
    if (primary) {
      const anyP = primary as any;
      const full = [anyP.firstName, anyP.fatherHusbandName, anyP.surname].filter(Boolean).join(' ').trim();
      setPaymentForm(prev => ({ ...prev, receiptName: full }));
    }
  };

  // Enhanced form validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Amount validation - allow negative amounts (for adjustments/reversals)
    if (!paymentForm.amount || parseFloat(paymentForm.amount) === 0 || isNaN(parseFloat(paymentForm.amount))) {
      errors.amount = 'Please enter a valid amount (negative allowed for adjustments)';
    }
    
    // Reference number validation
    if (!paymentForm.referenceNumber.trim()) {
      errors.referenceNumber = 'Reference number is required';
    }
    
    // Bank name validation
    if (!paymentForm.bankName.trim()) {
      errors.bankName = 'Bank name is required';
    }
    
    // Receipt number validation
    if (!paymentForm.receiptNo.trim()) {
      errors.receiptNo = 'Receipt number is required';
    }
    
    // Payment Terms is mandatory
    if (!paymentForm.paymentTerms) {
      errors.paymentTerms = 'Payment terms is required';
    }

    // Receive date (chequeReceivedDate) is mandatory for all methods
    if (!paymentForm.chequeReceivedDate) {
      errors.chequeReceivedDate = 'Receive date is required';
    }
    
    // Cheque-specific validations
    if (paymentForm.paymentMethod === 'cheque') {
      if (!paymentForm.instrumentDate) {
        errors.instrumentDate = 'Instrument date is required for cheque payments';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBooking) {
      setError('No booking selected');
      return;
    }
    
    if (!validateForm()) {
      setError('Please correct the errors below');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Use custom payment method if "other" is selected, otherwise use the selected method
      const finalPaymentMethod = paymentForm.paymentMethod === 'other' 
        ? customPaymentMethod.trim() || 'other' // Use custom or fallback to 'other'
        : paymentForm.paymentMethod;
      
      const paymentData: any = {
        bookingId: selectedBooking.id,
        amount: parseFloat(paymentForm.amount),
        paymentDate: paymentForm.paymentDate,
        paymentMethod: finalPaymentMethod as any, // Allow any string for custom payment methods
        referenceNumber: paymentForm.referenceNumber,
        bankName: paymentForm.bankName,
        description: paymentForm.description || undefined,
        receiptName: paymentForm.receiptName || undefined,
        receiptNo: paymentForm.receiptNo,
        chequeReceivedDate: paymentForm.chequeReceivedDate || undefined,
        instrumentDate: paymentForm.instrumentDate || undefined,
        chequeDepositDate: paymentForm.chequeDepositDate || undefined,
        remarks: paymentForm.remarks || undefined,
        paymentTerms: paymentForm.paymentTerms || undefined
      };

      // For non-cheque methods, clear instrument and deposit dates.
      // Send null (not undefined) so the field is included in the request body
      // and the backend actually clears it on edit — undefined is dropped from
      // JSON, which would leave a stale instrument date when switching
      // cheque -> IMPS/NEFT/etc.
      if (finalPaymentMethod !== 'cheque') {
        paymentData.instrumentDate = null;
        paymentData.chequeDepositDate = null;
      }

      if (parseFloat(paymentForm.amount) < 0) {
        // Validate allocation
        if (!isAllocationValid) {
          setAllocationErrors([
            `Total allocated (${totalAllocated.toLocaleString()}) must match return amount (${absAmount.toLocaleString()})`
          ]);
          return;
        }
        const allocationEntries = Object.entries(allocations)
          .filter(([, v]) => v > 0)
          .map(([id, amt]) => ({ sourceIncomeId: parseInt(id), allocatedAmount: amt }));
        if (allocationEntries.length === 0) {
          setAllocationErrors(['At least one allocation is required']);
          return;
        }
        paymentData.allocations = allocationEntries;
        await incomeService.createReturnIncome(paymentData);
      } else if (mode === 'edit' && id) {
        await incomeService.updateIncome(parseInt(id), paymentData);
      } else {
        await incomeService.createIncome(paymentData);
      }

      // Reset form and navigate back
      resetForm();
      navigate('/ledger/income');
    } catch (err: any) {
      // Check if this is a duplicate entry error
      if (err.response?.data?.error === 'DUPLICATE_ENTRY') {
        const message = err.response.data.message || 'Entry Already Exists';
        setError(message);
        
        // Show alert for better visibility
        alert(message);
        
        console.error('Duplicate entry detected:', err.response.data);
      } else {
        const errorMessage = err instanceof Error ? err.message : `Failed to ${mode === 'edit' ? 'update' : 'add'} payment`;
        setError(errorMessage);
        console.error('Payment submission error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Enhanced form reset
  const resetForm = () => {
    setPaymentForm({
      amount: '',
      paymentDate: formatDateForInput(new Date()),
      paymentMethod: 'cheque',
      referenceNumber: '',
      bankName: '',
      description: '',
      receiptName: '',
      receiptNo: '',
      chequeReceivedDate: formatDateForInput(new Date()),
      instrumentDate: '', // Blank after reset, user must enter manually
      chequeDepositDate: '',
      remarks: '',
      paymentTerms: ''
    });
    setCustomPaymentMethod('');
    setFormErrors({});
  };

  const getCustomerName = (booking: Booking) => {
    const primaryApplicant = booking.applicants.find(a => a.isPrimary) || booking.applicants[0];
    if (!primaryApplicant) return 'N/A';
    const anyApplicant = primaryApplicant as any;
    const parts = [
      anyApplicant.firstName || '',
      anyApplicant.fatherHusbandName || anyApplicant.middleName || anyApplicant.middle || '',
      anyApplicant.lastName || anyApplicant.surname || ''
    ].filter(Boolean);
    return parts.length ? parts.join(' ') : 'N/A';
  };

  const getPaymentMethodLabel = (method: string) => {
    const methods: Record<string, string> = {
      'cheque': 'Cheque',
      'neft': 'NEFT',
      'imps': 'IMPS',
      'rtgs': 'RTGS',
      'upi': 'UPI',
      'mb': 'MB',
      'transfer': 'Transfer'
    };
    return methods[method] || method.toUpperCase();
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project.id.toString());
    setSelectedProjectName(project.name);
    setProjectSearchTerm(project.name);
    setShowProjectDropdown(false);
    setError('');
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(projectSearchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      'approval_pending': 'status-pending',
      'approved': 'status-approved',
      'rejected': 'status-rejected'
    };
    
    const statusLabels: Record<string, string> = {
      'approval_pending': 'Pending',
      'approved': 'Approved',
      'rejected': 'Rejected'
    };

    return (
      <span className={`status-badge ${statusClasses[status] || ''}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  // Enhanced calculations with null safety
  const totalPaidAmount = paymentHistory
    .filter(payment => payment.status === 'approved')
    .reduce((sum, payment) => sum + (payment.amount || 0), 0);

  const pendingAmount = paymentHistory
    .filter(payment => payment.status === 'approval_pending')
    .reduce((sum, payment) => sum + (payment.amount || 0), 0);

  const remainingAmount = selectedBooking 
    ? (selectedBooking.totalAmount || 0) - totalPaidAmount
    : 0;

  return (
    <div className="page-container add-income-page">
      {/* Header removed as per requirement */}

      {/* Filter Section (Booking-style) - Only in add mode */}
      {mode === 'add' && (
        <SharedFilter
          show={showFilters}
          projectId={projectFilter}
          unitNumber={unitFilter}
          customerName={customerFilter}
          mobile={mobileFilter}
          onProjectChange={setProjectFilter}
          onUnitNumberChange={setUnitFilter}
          onCustomerNameChange={setCustomerFilter}
          onMobileChange={setMobileFilter}
          projects={projects}
          onApply={() => {
            const next = {
              project: projectFilter,
              wing: wingFilter,
              unit: unitFilter,
              customer: customerFilter,
              mobile: mobileFilter,
            };
            setAppliedFilters(next);
            setHasAppliedOnce(true);
            // Call with explicit filters to avoid async state lag
            applyFilters(next);
          }}
          onClear={() => {
            setProjectFilter('');
            setWingFilter('');
            setUnitFilter('');
            setCustomerFilter('');
            setMobileFilter('');
            setAppliedFilters({ project: '', wing: '', unit: '', customer: '', mobile: '' });
            setSearchResults([]);
            setSelectedBooking(null);
            setShowPaymentForm(false);
            setError('');
            setHasAppliedOnce(false);
          }}
        />
      )}

      {/* Search Results - Only show in add mode */}
      {mode === 'add' && searchResults.length > 0 && (
        <div className="search-results">
          <h3>Search Results ({searchResults.length} found)</h3>
          <div className="content-section">
            <table className="standard-table slim">
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Booking ID</th>
                  <th>Unit</th>
                  <th>Total Amount</th>
                  <th>Booking Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((booking) => (
                  <tr key={booking.id}>
                    <td>
                      <div className="customer-info">
                        <span className="customer-name">{getCustomerName(booking)}</span>
                        <span className="customer-phone">
                          {booking.applicants.find(a => a.isPrimary)?.contactNumber}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="booking-id">#{booking.bookingId}</span>
                    </td>
                    <td>
                      <div className="unit-info">
                        <span className="unit-number">{formatUnitDisplay(booking.unit.wing, booking.unit.unitNo)}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                        <span className="amount-main">{formatMainDBAmount(booking.saleDeedAmount || 0)}</span>
                        {isHiddenModeActive && (
                          <span className="amount-systems">{formatSystemsDBAmount((booking.chargesAmount || 0) + (booking.terraceAmount || 0) + (booking.otherWorkAmount || 0) + (booking.banakhatAmount || 0))}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="date-text">{formatDate(booking.bookingDate)}</span>
                    </td>
                    <td>
                      <button
                        onClick={() => handleSelectBooking(booking)}
                        className="add-payment-btn icon-only"
                        title="Add Income"
                      >
                        <FaPlus />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data state when filters applied but no bookings found */}
      {mode === 'add' && hasAppliedOnce && !searchLoading && searchResults.length === 0 && !selectedBooking && (
        <div className="search-results">
          <h3>Search Results</h3>
          <div className="content-section" style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
            No data
          </div>
        </div>
      )}

      {/* Selected Booking & Payment Form */}
      {selectedBooking && showPaymentForm && (
        <div className="payment-section">
          <div className="selected-booking" style={{ padding: '10px 16px' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Selected Booking</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', fontSize: '13px', lineHeight: '1.6' }}>
              <span><strong>Customer:</strong> {getCustomerName(selectedBooking)}</span>
              <span><strong>Booking:</strong> #{selectedBooking.bookingId}</span>
              <span><strong>Unit:</strong> {formatUnitDisplay(selectedBooking.unit.wing, selectedBooking.unit.unitNo)} ({selectedBooking.unit.unitType})</span>
              <span><strong>Project:</strong> {selectedBooking.unit?.project?.name || selectedBooking.project?.name || 'N/A'}</span>
              <span><strong>Sale Deed:</strong> {formatMainDBAmount(selectedBooking.saleDeedAmount || 0)}</span>
              {isHiddenModeActive && (
                <>
                  <span><strong>Charges:</strong> {formatSystemsDBAmount(selectedBooking.chargesAmount || 0)}</span>
                  <span><strong>Terrace:</strong> {formatSystemsDBAmount(selectedBooking.terraceAmount || 0)}</span>
                  <span><strong>Other Work:</strong> {formatSystemsDBAmount(selectedBooking.otherWorkAmount || 0)}</span>
                  <span><strong>Banakhat:</strong> {formatSystemsDBAmount(selectedBooking.banakhatAmount || 0)}</span>
                </>
              )}
              <span><strong>Total:</strong> {formatTotalAmount(selectedBooking.totalAmount)}</span>
              <span><strong>Paid:</strong> <span style={{color: '#27ae60'}}>{formatTotalAmount(totalPaidAmount)}</span></span>
              <span><strong>Pending:</strong> <span style={{color: '#f39c12'}}>{formatTotalAmount(pendingAmount)}</span></span>
              <span><strong>Remaining:</strong> <span style={{color: '#e74c3c'}}>{formatTotalAmount(remainingAmount)}</span></span>
            </div>
          </div>

          {/* Payer selection */}
          <div className="payment-history-section" style={{ padding: '8px 16px' }}>
            <h3 style={{ margin: '0 0 6px 0', fontSize: '14px' }}>Select Payer (Customer)</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                {selectedBooking.applicants.map((a, idx) => {
                  const anyA = a as any;
                  const full = [
                    anyA.firstName || '',
                    anyA.fatherHusbandName || anyA.middleName || anyA.middle || '',
                    anyA.lastName || anyA.surname || ''
                  ].filter(Boolean).join(' ');
                  const id = `payer-${idx}`;
                  return (
                    <label key={id} style={{display:'flex', alignItems:'center', gap: '5px', fontSize: '13px'}}>
                      <input
                        type="radio"
                        name="payer"
                        value={full}
                        checked={paymentForm.receiptName === full}
                        onChange={() => setPaymentForm(prev => ({ ...prev, receiptName: full }))}
                      />
                      <span>{full}</span>
                    </label>
                  );
                })}
            </div>
          </div>

          <div className="payment-form-section">
            {rejectedInfo && (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '12px 16px',
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                  color: '#991b1b',
                  borderRadius: '6px',
                  fontSize: '13px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <strong>This income was rejected.</strong>
                <span><strong>Reason:</strong> {rejectedInfo.reason}</span>
                <span style={{ color: '#7f1d1d' }}>
                  Update the details and save — it will be sent back to the approval queue.
                </span>
              </div>
            )}
            <h3>Payment Details</h3>
            <form onSubmit={handleAddPayment} className="payment-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Receipt Name</label>
                  <input
                    type="text"
                    value={paymentForm.receiptName}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, receiptName: e.target.value }))}
                    placeholder="Name of person giving payment (if different from customer)"
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label>Receipt No *</label>
                  <input
                    type="text"
                    value={paymentForm.receiptNo}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, receiptNo: e.target.value }))}
                    required
                    placeholder="Enter receipt number"
                    readOnly={isViewMode}
                  />
                </div>
                
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                    required
                    placeholder="Enter payment amount"
                    readOnly={isViewMode}
                  />
                </div>

                {/* Return Allocation UI - visible when amount is negative */}
                {showAllocationUI && availableIncomes.length > 0 && (
                  <div className="allocation-section full-width">
                    <div className="allocation-section-header">
                      <h3>Return Allocation</h3>
                      <span className={`allocation-badge ${isAllocationValid ? 'valid' : ''}`}>
                        ₹{totalAllocated.toLocaleString()} / ₹{absAmount.toLocaleString()}
                        {isAllocationValid ? ' ✓' : ` — ₹${(absAmount - totalAllocated).toLocaleString()} remaining`}
                      </span>
                    </div>
                    <p className="return-note">
                      Mark incomes to allocate against the return of <strong>₹{absAmount.toLocaleString()}</strong>
                    </p>
                    <div className="allocation-grid">
                      <div className="allocation-header">
                        <span>Mark</span>
                        <span>#</span>
                        <span>Date</span>
                        <span>Amount</span>
                        <span>Available</span>
                        <span>Allocated</span>
                      </div>
                      {availableIncomes
                        .filter(inc => inc.remainingBalance > 0)
                        .map(inc => {
                          const isMarked = (allocations[inc.id] || 0) > 0;
                          const allocated = allocations[inc.id] || 0;
                          return (
                            <div key={inc.id} className={`allocation-row ${isMarked ? 'marked' : ''}`}>
                              <span className="allocation-mark">
                                <input
                                  type="checkbox"
                                  id={`mark-${inc.id}`}
                                  checked={isMarked}
                                  onChange={e => handleMark(inc.id, e.target.checked, inc.remainingBalance)}
                                />
                                <label htmlFor={`mark-${inc.id}`} className="mark-label" />
                              </span>
                              <span className="allocation-id">{inc.receiptNo || `#${inc.id}`}</span>
                              <span className="allocation-date">{formatDate(inc.paymentDate)}</span>
                              <span className="allocation-amount">₹{inc.amount.toLocaleString()}</span>
                              <span className="allocation-remaining">₹{inc.remainingBalance.toLocaleString()}</span>
                              <span className={`allocation-allocated ${isMarked ? 'marked-amount' : 'zero-amount'}`}>
                                {isMarked ? `₹${allocated.toLocaleString()}` : '—'}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                    {allocationErrors.length > 0 && (
                      <div className="allocation-errors">
                        {allocationErrors.map((err, i) => (
                          <p key={i} className="error-text">{err}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Payment Date field is hidden as per requirement */}
                
                <div className="form-group">
                  <label>Payment Method *</label>
                  <select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => {
                      const nextMethod = e.target.value as 'cheque' | 'neft' | 'imps' | 'rtgs' | 'upi' | 'mb' | 'transfer' | 'other';
                      setPaymentForm(prev => ({ 
                        ...prev, 
                        paymentMethod: nextMethod,
                        // Clear instrument/deposit dates when not cheque
                        instrumentDate: nextMethod === 'cheque' ? prev.instrumentDate : '',
                        chequeDepositDate: nextMethod === 'cheque' ? prev.chequeDepositDate : ''
                      }));
                      // Clear custom payment method if switching away from "other"
                      if (nextMethod !== 'other') {
                        setCustomPaymentMethod('');
                      }
                    }}
                    required
                    disabled={isViewMode}
                  >
                    <option value="cheque">Cheque</option>
                    <option value="rtgs">RTGS</option>
                    <option value="neft">NEFT</option>
                    <option value="imps">IMPS</option>
                    <option value="upi">UPI</option>
                    <option value="mb">MB</option>
                    <option value="transfer">Transfer</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Custom Payment Method - only visible when "Other" is selected */}
                {paymentForm.paymentMethod === 'other' && (
                  <div className="form-group">
                    <label>Custom Payment Method *</label>
                    <input
                      type="text"
                      value={customPaymentMethod}
                      onChange={(e) => setCustomPaymentMethod(e.target.value)}
                      placeholder="Enter custom payment method"
                      required
                      readOnly={isViewMode}
                    />
                  </div>
                )}

                {/* Payment Terms */}
                <div className="form-group">
                  <label>Payment Terms *</label>
                  <select
                    value={paymentForm.paymentTerms}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentTerms: e.target.value }))}
                    disabled={isViewMode}
                    required
                  >
                    <option value="">-- Select Payment Terms --</option>
                    <option value="Down Payment">Down Payment</option>
                    <option value="Loan">Loan</option>
                    <option value="Installments">Installments</option>
                    <option value="Mixed Payment">Mixed Payment</option>
                  </select>
                  {formErrors.paymentTerms && (
                    <span className="error-text">{formErrors.paymentTerms}</span>
                  )}
                </div>

                {/* Date fields - receive date always; instrument/deposit only for cheque */}
                <div className="form-group">
                  <label>{paymentForm.paymentMethod === 'cheque' ? 'Cheque Received Date *' : 'Receive Date *'}</label>
                  <input
                    type="date"
                    value={paymentForm.chequeReceivedDate}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, chequeReceivedDate: e.target.value }))}
                    max={getMaxTransactionDate()}
                    required
                    readOnly={isViewMode}
                  />
                </div>

                {paymentForm.paymentMethod === 'cheque' && (
                  <>
                    <div className="form-group">
                      <label>Instrument Date * <span className="field-note">(Please enter date manually)</span></label>
                      <input
                        type="date"
                        value={paymentForm.instrumentDate}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, instrumentDate: e.target.value }))}
                        required
                        readOnly={isViewMode}
                        placeholder="Select instrument date"
                      />
                      {formErrors.instrumentDate && (
                        <span className="error-text">{formErrors.instrumentDate}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Cheque Deposit Date</label>
                      <input
                        type="date"
                        value={paymentForm.chequeDepositDate}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, chequeDepositDate: e.target.value }))}
                        max={getMaxTransactionDate()}
                        readOnly={isViewMode}
                      />
                    </div>
                  </>
                )}
                
                <div className="form-group">
                  <label>Reference Number *</label>
                  <input
                    type="text"
                    value={paymentForm.referenceNumber}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, referenceNumber: e.target.value }))}
                    placeholder="Cheque number, transaction ID, etc."
                    readOnly={isViewMode}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Bank Name *</label>
                  <input
                    type="text"
                    value={paymentForm.bankName}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, bankName: e.target.value }))}
                    placeholder="Bank name for electronic transfers"
                    readOnly={isViewMode}
                    required
                  />
                </div>
                
                <div className="form-group full-width">
                  <label>Remarks</label>
                  <textarea
                    value={paymentForm.remarks}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, remarks: e.target.value }))}
                    rows={2}
                    placeholder="Remarks or additional reference information"
                    readOnly={isViewMode}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={paymentForm.description}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    placeholder="Additional notes (optional)"
                    readOnly={isViewMode}
                  />
                </div>
              </div>
              
              <div className="form-actions">
                {!isViewMode && (
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={loading}
                  >
                    <FaPlus /> {loading ? 
                      (mode === 'edit' ? 'Updating...' : 'Adding...') : 
                      (mode === 'edit' ? 'Update Income' : 'Add Income')
                    }
                  </button>
                )}
                <button 
                  type="button" 
                  onClick={() => {
                    if (mode === 'edit' || mode === 'view') {
                      // Navigate back to income list page when in edit/view mode
                      navigate('/ledger/income');
                    } else {
                      // For add mode, just hide the payment form
                      setSelectedBooking(null);
                      setShowPaymentForm(false);
                    }
                  }}
                  className="cancel-btn"
                >
                  {isViewMode ? 'Back' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddIncome;
